import * as React from 'react';
import { createRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { rootRoute } from './__root';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (search: Record<string, unknown>): { token: string } => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: ResetPasswordPage,
});

const ResetPasswordFormSchema = z
  .object({
    newPassword: z.string().min(8, 'Use at least 8 characters'),
    confirmPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

type FormValues = z.infer<typeof ResetPasswordFormSchema>;

function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const { token } = useSearch({ from: resetPasswordRoute.id });
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(ResetPasswordFormSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      api.resetPassword({ token, newPassword: values.newPassword }),
    onSuccess: () => setDone(true),
    onError: (err) => {
      setErrorMessage(
        toMessage(err),
      );
    },
  });

  const onSubmit = (values: FormValues): void => {
    setErrorMessage(null);
    mutation.mutate(values);
  };

  if (!token) {
    return (
      <div className="mx-auto flex min-h-screen max-w-lg items-center px-4">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Reset link is incomplete</CardTitle>
            <CardDescription>
              This page needs the link from your email. Request a new one to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/forgot-password" className="text-sm underline">
              Request a new reset link
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-lg items-center px-4">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Choose a new password</CardTitle>
          <CardDescription>
            {done
              ? 'Your password has been changed.'
              : 'This link can be used once and expires 30 minutes after it was sent.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="flex flex-col gap-4">
              <Alert>
                <AlertTitle>Password changed</AlertTitle>
                <AlertDescription>
                  Every existing session was signed out. Sign in with your new password.
                </AlertDescription>
              </Alert>
              <Button onClick={() => void navigate({ to: '/login' })}>Go to sign in</Button>
            </div>
          ) : (
            <>
              {errorMessage ? (
                <Alert variant="destructive" className="mb-4">
                  <AlertTitle>Could not reset your password</AlertTitle>
                  <AlertDescription>
                    {errorMessage}{' '}
                    <Link to="/forgot-password" className="underline">
                      Request a new link
                    </Link>
                  </AlertDescription>
                </Alert>
              ) : null}

              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="newPassword">New password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    autoComplete="new-password"
                    {...form.register('newPassword')}
                  />
                  {form.formState.errors.newPassword ? (
                    <p className="text-xs text-[hsl(var(--destructive))]">
                      {form.formState.errors.newPassword.message}
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="confirmPassword">Confirm new password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    {...form.register('confirmPassword')}
                  />
                  {form.formState.errors.confirmPassword ? (
                    <p className="text-xs text-[hsl(var(--destructive))]">
                      {form.formState.errors.confirmPassword.message}
                    </p>
                  ) : null}
                </div>

                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? 'Saving…' : 'Set new password'}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
